import { BookingEntity } from 'Models/Entities';
import addOnStore from 'Models/AddOnStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { getTotalPassengerCountFromBooking } from 'Util/_HumanWritten/BookingEntity/GetTotalPassengerCount';
import { isBeforeNow } from 'Util/_HumanWritten/GeneralHelpers/DateTimeHelpers';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';
import { getMessageByKey } from 'Stores/LanguageStore';
import { getJourneyChangeBlockedReason } from 'Util/_HumanWritten/BookingWizard/BookingChangePermissions';
import { hasSeatingInfo } from '../../FerryBookingDetailedView/Sections/PassengerInfoBreakdown';
import { EditType } from '../../BookingDetailedView';

export interface EditOption {
	editType: EditType;
	label: string;
	/** Short example of what the user might change here, shown under the label. */
	description: string;
	iconName: string;
	/**
	 * When set, the option is listed but not selectable, and this is shown in place of the
	 * description. Offered rather than hidden so the customer (and the staff member fielding the
	 * call) can see the option exists and why it isn't available — a silently absent option just
	 * produces a support question.
	 */
	disabledReason?: string;
}

/**
 * Builds the list of editable items shown in the guided edit flow's "what would you like to
 * edit?" step. The visibility rules mirror each detail section's own render conditions so the
 * options always match what's actually displayed on the booking.
 *
 * Freight bookings return [] — their edit navigation isn't wired up (RedirectEditTo has no
 * freight cases), so they don't get a guided edit flow.
 *
 * `hasMultipleEventDateTimes` (event bookings only) controls whether the "Date & time" option is
 * offered — there's nothing to switch to when the series has a single occurrence, so it's hidden.
 */
export function getBookingEditOptions(booking: BookingEntity, hasMultipleEventDateTimes: boolean = true): EditOption[] {
	const typeOfBooking = booking.typeOfBooking();
	const options: EditOption[] = [];

	if (typeOfBooking === 'EVENT_BOOKING') {
		// The event wizard consolidates everything onto its tickets tab, so both options route
		// there (see OnEventEdit) — they're listed separately to match the booking's sections.
		// Only offer changing the date/time when the event has more than one occurrence to pick from.
		if (hasMultipleEventDateTimes) {
			options.push({
				editType: 'event-date-time',
				label: 'Date & time',
				description: 'Choose a different event date or session time.',
				iconName: 'calendar_today',
			});
		}
		options.push({
			editType: 'tickets',
			label: 'Tickets',
			description: 'Add, remove or change the tickets on this booking.',
			iconName: 'confirmation_number',
		});
		options.push({
			editType: 'event-contact',
			label: 'Contact details',
			description: 'Update the primary contact name, email and phone.',
			iconName: 'contact_page',
		});
		return options;
	}

	if (typeOfBooking === 'PASSENGER_ONLY' || typeOfBooking === 'VEHICLE') {
		options.push({
			editType: 'trip-time',
			label: 'Date or time',
			description: 'Select a ticket on a different date or time.',
			iconName: 'calendar_today',
		});

		// Lands on the tickets tab, where the route selector lives. Listed separately from
		// "Date or time" because it's a different kind of change — the price is re-quoted from
		// scratch and a refund may be due.
		// Same rule the wizard applies once you're in it (see SummaryToWizardUtils) — don't offer as
		// selectable an option the next screen disables. Staff reach this option on a partially
		// checked-in booking, because canEditBooking returns early for staff.
		//
		// Asked of the PAIR, matching the wizard: the user hasn't chosen one way vs both trips yet,
		// and the backend guards each leg separately.
		const otherLeg = booking.returnBooking ?? booking.returnBookingFor;

		options.push({
			editType: 'route-direction',
			label: 'Route or direction',
			description: hasEditableReturnLeg(booking)
				? 'Travel a different way. The return trip will be managed separately afterwards.'
				: 'Travel between different places, or the other way around.',
			iconName: 'swap_horiz',
			disabledReason: getJourneyChangeBlockedReason(booking, otherLeg),
		});

		if (getTotalPassengerCountFromBooking(booking) > 0) {
			options.push({
				editType: 'passenger',
				label: 'Passengers',
				description: 'Add or remove passengers from the booking.',
				iconName: 'person',
			});

			// When the app collects passenger info (names, ages, contact details), offer a
			// separate option to edit those details for the existing passengers.
			if (appConfigStore.passengerInfoEnabled) {
				options.push({
					editType: 'passenger-details',
					label: 'Passenger details',
					description: 'Update names, ages and contact details.',
					iconName: 'badge',
				});
			}

			// Offered on a booking that actually holds a fare type or a seat, rather than wherever
			// the feature is switched on: a booking sold before the vessel had fare types has
			// nothing to change, and the seating step would open on a list of blanks.
			//
			// Asked of both legs, because either vessel may be the one that sells them — the same
			// reason the wizard's own step gates on both.
			//
			// Passenger-only, because that is the only flow with a seating step to send them to:
			// getVehicleBookingTabs has no 'seating' path, so on a vehicle booking this option
			// would route to a step the wizard never builds and the booker would be walked forward
			// to whatever came next.
			if (
				typeOfBooking === 'PASSENGER_ONLY'
				&& appConfigStore.passengerClassesEnabled
				&& bookingHasSeating(booking)
			) {
				options.push({
					editType: 'seating',
					label: 'Fare type or seat',
					description: 'Change the fare type or seat for each passenger.',
					iconName: 'airline_seat_recline_normal',
				});
			}
		}

		if (booking.hasVehicle()) {
			// Driver details are edited on the same wizard tab as the vehicle, so they're
			// covered by this option rather than the passengers one.
			options.push({
				editType: 'vehicle',
				label: 'Vehicle details',
				description: getVehicleEditDescription(),
				iconName: 'directions_car',
			});
		}

		// Show the add-ons option whenever the app offers add-ons for this booking type on these
		// trips — even if none are currently on the booking — so the user can add one. (Requires
		// the add-on store to be loaded; callers should ensure that, e.g. via useAddOns.)
		if (appHasAddOnsForBooking(booking)) {
			options.push({
				editType: 'add-on',
				// Use the same configurable language as the add-ons section header so the
				// wording stays consistent (and per-tenant configurable) across the app.
				label: getMessageByKey('booking-details__add-ons-section__label', 'Add ons'),
				description: getMessageByKey(
					'booking-details__add-ons-edit-option__description',
					'Add or remove extras on your booking.',
				),
				iconName: 'library_add',
			});
		}

		// Catch-all for anything not covered above — booking name, the additional information
		// note, and admin-configured dynamic fields — shown only when any of those are set up.
		if (appHasOtherEditableFields()) {
			options.push({
				editType: 'other',
				label: 'Other',
				description: 'Booking name and additional information.',
				iconName: 'more_horiz',
			});
		}

		return options;
	}

	return options;
}

/**
 * True when this booking already holds a fare type or a seat, on either of its legs.
 *
 * <p>Read off the booked summary the detailed view is already showing, so the option appears for
 * exactly the bookings whose fare types and seats are on screen above it.</p>
 */
function bookingHasSeating(booking: BookingEntity): boolean {
	const otherLeg = booking.returnBooking ?? booking.returnBookingFor;
	return hasSeatingInfo(booking.GetAllPassengerInfosFromBookedSummary())
		|| (isNotNullOrUndefined(otherLeg) && hasSeatingInfo(otherLeg.GetAllPassengerInfosFromBookedSummary()));
}

/**
 * Builds the "Vehicle details" option description, mentioning the trailer only when trailers are
 * enabled and the rego (using the app's configured rego label) only when rego capture is enabled.
 */
function getVehicleEditDescription(): string {
	const items = ['vehicle'];
	if (appConfigStore.trailersEnabled) {
		items.push('trailer');
	}
	items.push('dimensions');
	if (appConfigStore.regoEnabled) {
		items.push(getMessageByKey('vehicle-tab__rego__label', 'Rego').toLowerCase());
	}
	return `Change the ${items.join(', ')} or driver details.`;
}

/**
 * True when there are "other" editable details set up for the app — the primary booking name,
 * the additional information note, or any active admin-configured dynamic booking field.
 * (Requires the dynamic fields store to be loaded; callers should ensure that.)
 */
function appHasOtherEditableFields(): boolean {
	if (appConfigStore.collectPrimaryBookingNameEnabled || appConfigStore.bookingNoteEnabled) {
		return true;
	}
	return useDynamicBookingFieldsStore.getState().fields.some(field => !field.archived);
}

/**
 * True when the app offers add-ons applicable to this booking's type on its trip(s) — regardless
 * of whether the booking currently has any. Mirrors the wizard's add-ons tab visibility logic
 * (addOnStore.hasXAddOns). Add-on ids already on the booking are passed through so add-ons that
 * have since been disabled still keep the option available.
 */
function appHasAddOnsForBooking(booking: BookingEntity): boolean {
	if (!booking.bookedSummary?.ferryTrip) {
		return false;
	}

	const returnLeg = booking.returnBooking ?? booking.returnBookingFor;
	const trips: SelectedTrips = {
		departingTrip: booking.bookedSummary.ferryTrip,
		returningTrip: returnLeg?.bookedSummary?.ferryTrip,
	};

	const existingAddOnIds = (booking.bookedSummary.additionalBookingOptions ?? [])
		.map(x => x.option?.id)
		.filter((id): id is string => isNotNullOrUndefined(id));

	switch (booking.typeOfBooking()) {
		case 'VEHICLE':
			return addOnStore.hasVehicleAddOns(trips, existingAddOnIds);
		case 'PASSENGER_ONLY':
			return addOnStore.hasPassengerAddOns(trips, existingAddOnIds);
		default:
			return false;
	}
}

/**
 * True when the booking has a linked return leg that can also be altered. Mirrors the
 * same-named gate inside OnEdit so the guided flow only offers the "this trip / both trips"
 * choice when editing the return is actually possible.
 */
export function hasEditableReturnLeg(booking: BookingEntity): boolean {
	if (booking.desynced) {
		return false;
	}

	const returnBookingInfo = booking.returnBooking ?? booking.returnBookingFor;
	if (!returnBookingInfo || !returnBookingInfo.bookedSummary) {
		return false;
	}

	const returnBookingClosed = returnBookingInfo.bookedSummary.ferryTrip.isClosedForSpecificBookingType(
		booking.typeOfBooking(),
	);

	return (
		returnBookingInfo.bookingStatus === 'BOOKED' &&
		!returnBookingInfo.checkedIn &&
		isNullOrUndefined(returnBookingInfo.checkInStatusId) &&
		!returnBookingClosed &&
		!isBeforeNow(returnBookingInfo.bookedSummary.ferryTrip.departureDateTime)
	);
}
